import request from '@/utils/request'

export interface EntitlementRule {
  id?: number
  ruleName: string
  ruleKey: string
  triggerType: string
  conditionsJson: Record<string, any>
  featureGrantsJson: any[]
  priority: number
  isActive: boolean
  validFrom?: Date | string | null
  validUntil?: Date | string | null
  description?: string
  createdBy?: string
  createdAt?: Date
  updatedAt?: Date
}

export interface CreateRuleRequest {
  name: string
  description?: string
  triggerType: string
  conditions: Record<string, any>
  featureGrants: any[]
  priority: number
  isActive?: boolean
}

export interface TestRuleContext {
  product_type?: string
  purchase_type?: string
  purchase_date?: Date | string
  [key: string]: any
}

export const listRules = (triggerType?: string) =>
  request({
    url: '/entitlement-rules',
    method: 'get',
    params: { triggerType }
  })

export const getRule = (id: number) =>
  request({
    url: `/entitlement-rules/${id}`,
    method: 'get'
  })

export const createRule = (data: CreateRuleRequest) =>
  request({
    url: '/entitlement-rules',
    method: 'post',
    data
  })

export const updateRule = (id: number, data: Partial<CreateRuleRequest>) =>
  request({
    url: `/entitlement-rules/${id}`,
    method: 'put',
    data
  })

export const deleteRule = (id: number) =>
  request({
    url: `/entitlement-rules/${id}`,
    method: 'delete'
  })

export const testRule = (id: number, context: TestRuleContext) =>
  request({
    url: `/entitlement-rules/${id}/test`,
    method: 'post',
    data: context
  })

export const evaluateRules = (context: TestRuleContext, triggerType: string) =>
  request({
    url: '/entitlement-rules/evaluate',
    method: 'post',
    data: { context, triggerType }
  })

export const getMatchingRules = (context: TestRuleContext, triggerType: string) =>
  request({
    url: '/entitlement-rules/match',
    method: 'post',
    data: { context, triggerType }
  })

// Stats & Monitoring

export interface RuleStatsSummary {
  totalEvaluations: number
  totalMatched: number
  matchRate: number
  totalGrants: number
  totalErrors: number
  avgDurationMs: number
}

export interface RuleStatsByTrigger {
  triggerType: string
  total: number
  matched: number
  avgDurationMs: number
}

export interface RuleStatsByRule {
  ruleId: number
  ruleName: string
  total: number
  matched: number
  avgDurationMs: number
}

export interface RuleStats {
  days: number
  summary: RuleStatsSummary
  byTriggerType: RuleStatsByTrigger[]
  byRule: RuleStatsByRule[]
}

export interface RuleExecutionLogEntry {
  id: number
  ruleId: number
  ruleName: string
  triggerType: string
  userId: string | null
  matched: boolean
  conditionsContext: Record<string, any> | null
  featuresGranted: any[] | null
  errorMessage: string | null
  executionDurationMs: number | null
  sourceReference: string | null
  created_at: string
}

export const getRuleStats = (days?: number) =>
  request({
    url: '/entitlement-rules/stats',
    method: 'get',
    params: { days }
  })

export const getRuleExecutionHistory = (params?: {
  page?: number
  limit?: number
  ruleId?: number
  triggerType?: string
  matched?: string
  userId?: string
}) =>
  request({
    url: '/entitlement-rules/execution-history',
    method: 'get',
    params
  })
